import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ReferralModal from "../components/ReferralModal";
import apiService from "../services/api";
import { Users, UserPlus, TrendingUp, DollarSign, ArrowRight, Search, ChevronRight, Loader2, Globe, Share2, Award } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Team() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({ total_team_size: 0, direct_referrals_count: 0, team_volume: 0, total_commission_earned: 0 });
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);

  useEffect(() => {
    apiService.get("/users/team").then(res => {
      if (res.success) { setStats(res.data.stats); setPartners(res.data.partners || []); }
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = (partners || []).filter(p =>
    (p.username && p.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.email && p.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const statCards = [
    { label: t('team.total_network'), value: stats.total_team_size.toLocaleString(), icon: <Users size={22} />, color: 'indigo', bg: 'bg-indigo-500/10', border: 'border-indigo-500/20', text: 'text-indigo-400' },
    { label: t('team.direct_partners'), value: stats.direct_referrals_count.toLocaleString(), icon: <UserPlus size={22} />, color: 'cyan', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400' },
    { label: t('team.global_volume'), value: `$${stats.team_volume.toLocaleString()}`, icon: <TrendingUp size={22} />, color: 'emerald', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400' },
    { label: t('team.total_commissions'), value: `$${stats.total_commission_earned.toLocaleString()}`, icon: <DollarSign size={22} />, color: 'amber', bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white font-inter">
      <Header />

      <section className="relative pt-28 md:pt-32 pb-10 overflow-hidden">
        <div className="absolute top-0 left-0 w-full max-w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                <Globe size={12} className="text-indigo-400" />
                <span className="text-[9px] md:text-[10px] font-black text-indigo-400 uppercase tracking-widest leading-none">{t('team.network_explorer')}</span>
              </div>
              <h1 className="text-4xl md:text-7xl font-black font-outfit uppercase tracking-tight">
                {t('team.my_network').split(' ').slice(0, -1).join(' ')} <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">{t('team.my_network').split(' ').slice(-1)}</span>
              </h1>
              <p className="text-slate-400 font-medium text-lg">{t('team.network_subtitle')}</p>
            </div>
            <button onClick={() => setIsReferralModalOpen(true)}
              className="flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs uppercase tracking-widest transition-all shadow-[0_8px_24px_rgba(99,102,241,0.3)] group">
              <Share2 size={16} className="group-hover:scale-110 transition-transform" />
              {t('team.direct_invitation')}
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {statCards.map((s, i) => (
              <div key={i} className="group relative overflow-hidden rounded-[20px] bg-slate-900/60 border border-white/5 p-6 hover:border-indigo-500/30 transition-all duration-300 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl ${s.bg} border ${s.border} ${s.text} group-hover:scale-110 transition-transform`}>{s.icon}</div>
                    <span className="text-[9px] font-black text-slate-600 uppercase tracking-[0.2em]">{s.label}</span>
                  </div>
                  <div className="text-3xl font-black font-outfit text-white tracking-tight tabular-nums">{s.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="pb-24 relative">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-slate-900/60 via-slate-900/30 to-indigo-900/10 border border-white/5 shadow-[0_20px_60px_-16px_rgba(2,6,23,0.5)]">
            <div className="p-6 md:p-8 border-b border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-400"><Award size={16} /></div>
                <h3 className="text-lg font-black font-outfit uppercase tracking-tight">{t('team.recent_partners')}</h3>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input type="text" placeholder={t('team.search_partner')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-slate-950 border border-white/5 rounded-xl pl-10 pr-4 py-3 text-xs font-bold text-white focus:border-indigo-500/50 outline-none transition-all placeholder:text-slate-600" />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-20 flex flex-col items-center gap-4">
                  <Loader2 size={40} className="animate-spin text-indigo-500" />
                  <p className="text-slate-500 font-black uppercase tracking-[0.3em] text-[10px]">{t('team.loading_network')}</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="bg-white/[0.01]">
                      {[t('team.partner'), t('team.level'), t('team.status'), t('team.volume'), t('team.joined'), ''].map((h, i) => (
                        <th key={i} className="text-left p-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filtered.length > 0 ? filtered.map((p, i) => (
                      <tr key={i} className="hover:bg-white/[0.02] transition-colors group cursor-default">
                        <td className="p-6">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center text-xs font-black shadow-inner uppercase">
                              {p.username ? p.username.charAt(0) : '?'}
                            </div>
                            <div>
                              <p className="text-sm font-black text-white">{p.username}</p>
                              <p className="text-[10px] text-slate-500 font-medium">{p.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-6"><span className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('team.level')} {p.level}</span></td>
                        <td className="p-6">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${p.status === 'active' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-rose-500'} animate-pulse`} />
                            <span className={`text-[10px] font-black uppercase tracking-widest ${p.status === 'active' ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {p.status === 'active' ? t('team.active') : t('team.inactive')}
                            </span>
                          </div>
                        </td>
                        <td className="p-6"><span className="font-black font-outfit text-white tabular-nums">${parseFloat(p.personal_volume || 0).toLocaleString()}</span></td>
                        <td className="p-6 text-xs font-bold text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="p-6 text-right"><button className="p-2 text-slate-600 hover:text-indigo-400 transition-colors"><ChevronRight size={20} /></button></td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} className="p-20 text-center text-slate-500 font-black uppercase tracking-widest text-xs">{t('team.no_partners')}</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-6 border-t border-white/5 text-center">
              <button className="text-[10px] font-black text-slate-500 uppercase tracking-widest hover:text-white transition-colors flex items-center justify-center gap-2 mx-auto">
                {t('team.view_full_network')} <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ReferralModal isOpen={isReferralModalOpen} onClose={() => setIsReferralModalOpen(false)} />
    </div>
  );
}